<template>
  <div>
    <h4>Categories and Shift validation</h4>
    <p>Here you can enable the visibility of categories/ types for the board.</p>
    <p>
      Additionally you can enable validation for the board and choose which Task Types should be included in Shift
      <br />
      validation. When the shift is validated, for each task type which was not used during the shift, a reminder task will be created.
    </p>
    <p>
      The checkbox "relevant for shift report" controls which tasks will be automatically included in the relevant items of the shift
      report.
    </p>
    <div class="validation mt-4" @change="isDirty = true">
      <div class="row" v-if="settings.enableShifts">
        <div class="col-12">
          <div class="label">Enable Shift Validation</div>
          <checkbox-with-label
            :label="'Enable Shift Validation'"
            v-model="settings.isValidationEnabled"
            @input="() => (isDirty = true)"
          ></checkbox-with-label>
        </div>
      </div>

      <div class="mt-4">
        <div class="label">Relevant Task Types</div>
        <div class="container-fluid">
          <div class="row">
            <div class="types-box col-12">
              <div class="row pt-2 mb-2">
                <div class="col-6">
                  <label class="font-weight-bold">Category/Type</label>
                </div>
                <div class="col-2">
                  <label class="font-weight-bold">Available</label>
                </div>
                <div class="col-2" v-if="settings.enableShifts">
                  <label class="font-weight-bold">Relevant for shift validation</label>
                </div>
                <div class="col-2" v-if="settings.enableShifts">
                  <label class="font-weight-bold">Relevant for shift report</label>
                </div>
              </div>
              <div class="row">
                <div class="col-2 offset-6">
                  <checkbox-with-label
                    :label="selectAllAvailable ? 'Unselect all' : 'Select all'"
                    v-model="selectAllAvailable"
                    @input="setAvailableStateForAll"
                  ></checkbox-with-label>
                </div>

                <div class="col-2" v-if="settings.enableShifts">
                  <checkbox-with-label
                    :label="selectAllRelevant ? 'Unselect all' : 'Select all'"
                    v-model="selectAllRelevant"
                    @input="setRelevantStateForAll"
                    :disabled="!settings.isValidationEnabled"
                  ></checkbox-with-label>
                </div>
                <div class="col-2" v-if="settings.enableShifts">
                  <checkbox-with-label
                    :label="selectAllRelevant ? 'Unselect all' : 'Select all'"
                    v-model="selectAllRelevant"
                    @input="setRelevantForShiftReportStateForAll"
                  ></checkbox-with-label>
                </div>
              </div>
              <div class="row" v-for="cat in categories" :key="cat.id">
                <div class="col-12">
                  <div>
                    <h6 class="font-weight-bold">{{ cat.text }}</h6>
                  </div>
                  <div class="row" v-for="type in cat.items" :key="type.typeId">
                    <div class="col-6">
                      <label class="ml-3">
                        {{ type.text }}
                      </label>
                    </div>
                    <div class="col-2">
                      <checkbox-with-label
                        :label="'Available'"
                        v-model="type.isAvailable"
                        @input="() => processAvailableChanges(type)"
                      ></checkbox-with-label>
                    </div>
                    <div class="col-2">
                      <checkbox-with-label
                        :label="'Relevant'"
                        v-model="type.isRelevant"
                        @input="processRelevantChanges(type)"
                        :disabled="!settings.isValidationEnabled"
                      ></checkbox-with-label>
                    </div>

                    <div class="col-2">
                      <checkbox-with-label
                        :label="'Relevant'"
                        v-model="type.isRelevantForShiftReport"
                        @input="processRelevantForShiftReportChanges()"
                      ></checkbox-with-label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr />

        <div class="d-flex justify-content-end">
          <button :disabled="!isDirty" class="btn btn-save btn-sm btn-success" @click="save">
            <i class="far fa-save"></i>
            Save changes
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { SettingsStore, SettingsModule } from '@/store/settings';
  import { BoardStore, BoardModule } from '@/store/board';
  import { BoardItemValidationTypeDto } from '@/apiclient/apiclient_generated';

  @Component({
    components: {},
  })
  export default class ShiftValidation extends Vue {
    private settingsStore: SettingsModule = SettingsStore;
    private boardStore: BoardModule = BoardStore;
    private boardId = '';
    private isDirty = false;
    private categories = [];
    private selectAllRelevant = false;
    private selectAllAvailable = false;

    async mounted() {
      this.boardId = this.$route.params.id;

      await this.boardStore.loadBoardCategories();
      await this.settingsStore.loadShiftValidationSettings(this.boardId);

      const types = this.settings.types;

      this.boardStore.groupedSelect.forEach((cat) => {
        cat.items.forEach((t: any) => {
          const setting: BoardItemValidationTypeDto = types.find((_) => _.typeId == t.id);

          t.isRelevant = setting ? setting.isRelevant : false;
          t.isAvailable = setting ? setting.isAvailable : false;
          t.isRelevantForShiftReport = setting ? setting.isRelevantForShiftReport : false
        });
        this.categories.push(cat);
      });
    }

    get settings() {
      return this.settingsStore.shiftValidationSettings;
    }

    processRelevantChanges(item) {
      if (item.isRelevant) {
        item.isAvailable = item.isRelevant;
      }
      this.isDirty = true;
    }

    processRelevantForShiftReportChanges() {
      this.isDirty = true;
    }

    processAvailableChanges(item) {
      if (!item.isAvailable) {
        item.isRelevant = false;
      }
      this.isDirty = true;
    }

    async save() {
      const relevantTypes: BoardItemValidationTypeDto[] = this.categories
        .map((cat) => {
          return cat.items
            .filter((_) => _.isRelevant || _.isAvailable || _.isRelevantForShiftReport)
            .map((_) => {
              const dto = new BoardItemValidationTypeDto();
              dto.isRelevant = _.isRelevant;
              dto.isAvailable = _.isAvailable;
              dto.isRelevantForShiftReport = _.isRelevantForShiftReport;
              dto.typeId = _.id;
              return dto;
            });
        })
        .filter((_) => _ && _.length)
        .flat();

      await this.settingsStore.updateShiftValidationSettings(relevantTypes);

      this.isDirty = false;
    }

    setRelevantStateForAll(state: boolean) {
      this.categories.forEach((cat) => {
        cat.items.forEach((t) => {
          t.isRelevant = state;
          if (t.isRelevant && !t.isAvailable) {
            t.isAvailable = true;
          }
        });
      });
    }

    setRelevantForShiftReportStateForAll(state: boolean) {
      this.categories.forEach((cat) => {
        cat.items.forEach((t) => {
          t.isRelevantForShiftReport = state;
        });
      });
    }

    setAvailableStateForAll(state: boolean) {
      this.categories.forEach((cat) => {
        cat.items.forEach((t) => (t.isAvailable = state));
      });
    }
  }
</script>

<style scoped lang="scss">
  .types-box {
    border-radius: 4px;
    background-color: white;
    min-height: 300px;
    max-height: 500px;
    overflow-y: scroll;
    border: solid 1px #eaeaea;
  }
</style>